import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { getApiBaseUrl } from "../utils/apiConfig";



const Settings = () => {
  const [user, setUser] = useState(null);
  const [organization, setOrganization] = useState(null);

  const [integrations, setIntegrations] = useState({
    slack: { connected: false, status: "" }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [pendingSlackCode, setPendingSlackCode] = useState(null);
  const [isJoiningChannels, setIsJoiningChannels] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Retrieve user data from localStorage
    const userData = localStorage.getItem("user");
    if (userData && userData !== "undefined") {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
        if (parsedUser.organizations && parsedUser.organizations.length > 0) {
          fetchOrganization(parsedUser.organizations[0].organizationId);
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    // Check if there's a code parameter in the URL (for OAuth callback)
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    if (code && state === 'slack') {
      if (organization) {
        // If organization is loaded, handle the callback right away
        handleSlackCallback(code);
      } else {
        // Otherwise, store the code to be processed when organization loads
        setPendingSlackCode(code);
        // Remove code from URL to prevent multiple attempts
        navigate('/settings', { replace: true });
      }
    }
  }, [location, organization]);

  useEffect(() => {
    if (organization) {
      fetchIntegrationStatus();
      
      // Process any pending Slack code after organization is loaded
      if (pendingSlackCode) {
        handleSlackCallback(pendingSlackCode);
        setPendingSlackCode(null);
      }
    }
  }, [organization, pendingSlackCode]);

  const fetchIntegrationStatus = async () => {
    if (!organization) return;
    
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const apiUrl = getApiBaseUrl();
      const response = await axios.get(
        `${apiUrl}/integrations/status/${organization._id || organization.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      setIntegrations(response.data);
    } catch (error) {
      console.error("Error fetching integration status:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSlackCallback = async (code) => {
    if (!organization) {
      console.error("Cannot handle Slack callback: organization not loaded");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      const apiUrl = getApiBaseUrl();
      await axios.post(
        `${apiUrl}/integrations/slack/callback`,
        { 
          code,
          organizationId: organization._id || organization.id
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // Refresh integration status
      fetchIntegrationStatus();
      
      // Redirect to dashboard after successful integration
      navigate("/dashboard");
    } catch (error) {
      console.error("Error handling Slack callback:", error);
    }
  };



  const fetchOrganization = async (organizationId) => {
    try {
      const token = localStorage.getItem("token");
      const apiUrl = getApiBaseUrl();
      const response = await axios.get(`${apiUrl}/organization/${organizationId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setOrganization(response.data);
    } catch (error) {
      console.error("Error fetching organization data:", error);
    }
  };

  const initiateSlackOAuth = async () => {
    if (integrations.slack.connected) {
      // If already connected, disconnect
      try {
        const token = localStorage.getItem("token");
        const apiUrl = getApiBaseUrl();
        
        // Make sure we have a valid organization ID
        const orgId = organization?._id || organization?.id;
        if (!orgId) {
          console.error("Cannot disconnect Slack: Missing organization ID");
          return;
        }
        
        await axios.post(`${apiUrl}/integrations/slack/disconnect`,
          { organizationId: orgId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        // Refresh integration status
        fetchIntegrationStatus();
      } catch (error) {
        console.error("Error disconnecting Slack:", error);
      }
    } else {
      // If not connected, initiate OAuth flow
      try {
        const token = localStorage.getItem("token");
        const apiUrl = getApiBaseUrl();
        const response = await axios.get(`${apiUrl}/integrations/slack/authorize`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        // Redirect to Slack authorization page
        window.location.href = response.data.authUrl;
      } catch (error) {
        console.error("Error initiating Slack OAuth:", error);
      }
    }
  };

  const handleJoinChannels = async () => {
    if (!organization) {
      console.error("Cannot join channels: organization not loaded");
      return;
    }
    
    setIsJoiningChannels(true);
    try {
      const token = localStorage.getItem("token");
      const apiUrl = getApiBaseUrl();
      const orgId = organization._id || organization.id;
      
      const response = await axios.post(
        `${apiUrl}/integrations/slack/join-channels/${orgId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      console.log("Channel joining completed:", response.data);
      alert("Successfully attempted to join all public channels! Check console for details.");
    } catch (error) {
      console.error("Error joining channels:", error);
      alert("Error joining channels. Please try again.");
    } finally {
      setIsJoiningChannels(false);
    }
  };



  return (
    <div className="h-screen h-dvh overflow-hidden flex flex-row bg-bg-gradient-dash">
      <Sidebar 
        user={user} 
        organization={organization} 
        organizations={[]} 
      />
      <div className="flex-1 overflow-y-auto flex flex-col items-center p-6">
        <div className="w-full max-w-[768px] flex flex-col gap-6">
          <h1 className="text-[20px] font-normal text-black-0">Settings</h1>
          
          {/* Organization Section */}
          <div className="flex items-center justify-between">
            <h2 className="text-base font-normal text-black-0">Organization</h2>
            <span className="text-sm text-grey-80 font-medium">
              {organization?.name || 'Loading...'}
            </span>
          </div>

          {/* Slack Integration Section */}
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <div className="w-6 h-6 border-2 border-grey-40 border-t-brand rounded-full animate-spin"></div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <h2 className="text-base font-normal text-black-0">Slack integration</h2>
                {integrations.slack.connected ? (
                  integrations.slack.status && (
                    <p className="text-[14px] text-grey-60 font-normal">
                      {integrations.slack.status}
                    </p>
                  )
                ) : (
                  <p className="text-[14px] text-grey-60 font-normal">
                    Not connected
                  </p>
                )}
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={initiateSlackOAuth}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    integrations.slack.connected
                      ? "bg-white border border-grey-30 text-black-0 hover:bg-grey-10"
                      : "bg-brand text-white hover:bg-brand-dark"
                  }`}
                >
                  {integrations.slack.connected ? "Disconnect" : "Connect"}
                </button>
                {integrations.slack.connected && (
                  <button
                    onClick={handleJoinChannels}
                    disabled={isJoiningChannels}
                    className={`w-[186px] px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      isJoiningChannels 
                        ? "bg-grey-10 text-grey-60 cursor-not-allowed"
                        : "bg-brand text-white hover:bg-brand-dark"
                    }`}
                  >
                    {isJoiningChannels ? "Joining..." : "Join all public channels"}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* AI Provider Configuration - managed on Integrations page */}
          <div className="mt-4 flex items-center justify-between">
            <div className="flex flex-col">
              <h2 className="text-base font-normal text-black-0">AI Providers</h2>
              <p className="text-[14px] text-grey-60 font-normal">
                Manage OpenAI, Anthropic, Gemini, Grok, and Azure OpenAI
              </p>
            </div>
            <button
              onClick={() => navigate('/integrations')}
              className="px-4 py-2 rounded-md text-sm font-medium bg-white border border-grey-30 text-black-0 hover:bg-grey-10 transition-colors"
            >
              Manage
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings; 